import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Logo from "./ui/Logo";
import {
  getAppLabel,
  getFallbackPolicy,
  getPrivacyPolicyApiBase,
} from "../lib/privacyPolicies";

const PrivacyPolicyPage = () => {
  const { policyId = "mobile-app" } = useParams();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    let active = true;
    const apiBase = getPrivacyPolicyApiBase();

    const loadPolicy = async () => {
      try {
        setLoading(true);
        setError("");
        setUsingFallback(false);

        const response = await fetch(`${apiBase}/api/public/privacy-policies/${policyId}`);
        if (!response.ok) {
          throw new Error("Nao foi possivel carregar a politica de privacidade.");
        }

        const data = await response.json();
        if (active) {
          setPolicy(data);
        }
      } catch (err) {
        if (active) {
          const fallback = getFallbackPolicy(policyId);
          if (fallback) {
            setPolicy(fallback);
            setUsingFallback(true);
            setError("");
          } else {
            setError(err.message || "Erro ao carregar a politica de privacidade.");
          }
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadPolicy();
    return () => {
      active = false;
    };
  }, [policyId]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="mb-8 flex items-center justify-between gap-4 border-b border-white/10 pb-6">
          <Logo variant="full" theme="dark" iconClassName="h-10 w-auto" wordClassName="text-2xl" />
          <div className="text-right text-sm text-slate-400">
            <div>Politica de Privacidade</div>
            <div>{getAppLabel(policyId)}</div>
          </div>
        </div>

        {loading && (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-center text-slate-300">
            Carregando politica de privacidade...
          </div>
        )}

        {!loading && error && (
          <div className="rounded-3xl border border-rose-500/30 bg-rose-500/10 p-8">
            <p className="text-lg font-semibold text-rose-200">Nao foi possivel carregar a politica.</p>
            <p className="mt-2 text-sm text-rose-100/80">{error}</p>
          </div>
        )}

        {!loading && policy && (
          <div className="space-y-8">
            {usingFallback && (
              <section className="rounded-3xl border border-amber-400/25 bg-amber-400/10 p-5 text-sm text-amber-100">
                A pagina foi carregada com a versao local de contingencia. O endpoint publico da API nao respondeu neste acesso.
              </section>
            )}

            <section className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 shadow-2xl shadow-black/20">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-300">
                    {policy.app_name}
                  </p>
                  <h1 className="mt-3 text-3xl font-semibold tracking-tight text-white">
                    Politica de Privacidade
                  </h1>
                  <p className="mt-4 max-w-3xl text-base leading-7 text-slate-300">
                    {policy.summary}
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-slate-900/70 p-4 text-sm text-slate-300">
                  <div>
                    <span className="block text-xs uppercase tracking-[0.18em] text-slate-500">Vigencia</span>
                    <span>{policy.effective_date}</span>
                  </div>
                  <div className="mt-3">
                    <span className="block text-xs uppercase tracking-[0.18em] text-slate-500">Ultima atualizacao</span>
                    <span>{policy.last_updated}</span>
                  </div>
                  <div className="mt-3">
                    <span className="block text-xs uppercase tracking-[0.18em] text-slate-500">Contato</span>
                    <a className="text-cyan-300 hover:text-cyan-200" href={`mailto:${policy.contact_email}`}>
                      {policy.contact_email}
                    </a>
                  </div>
                </div>
              </div>
            </section>

            {policy.sections.map((section) => (
              <section
                key={section.title}
                className="rounded-3xl border border-white/10 bg-white/[0.03] p-8"
              >
                <h2 className="text-xl font-semibold text-white">{section.title}</h2>
                <div className="mt-4 space-y-4">
                  {section.content.map((paragraph, index) => (
                    <p key={`${section.title}-${index}`} className="text-sm leading-7 text-slate-300">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>
            ))}

            <div className="flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6 text-sm text-slate-400">
              <span>GridProfile / NeuroSynx</span>
              <div className="flex gap-4">
                <Link className="text-cyan-300 hover:text-cyan-200" to="/politica-de-privacidade/mobile-app">
                  Grid Profile
                </Link>
                <Link className="text-cyan-300 hover:text-cyan-200" to="/politica-de-privacidade/ponto-app">
                  GridProfile Ponto
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
